import React, { createContext, useContext, useState, ReactNode } from "react";

import { ColumnMapping, Client, ValidationError } from "0.Mine/3.Interfaces/Client";
interface ImportContextProps {
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  file: File | null;
  setFile: (file: File | null) => void;
  columns: string[];
  setColumns: (cols: string[]) => void;
  fileData: any[];
  setFileData: (data: any[]) => void;
  columnMapping: ColumnMapping[];
  setColumnMapping: React.Dispatch<React.SetStateAction<ColumnMapping[]>>;
  initialMapping: ColumnMapping[];
  setInitialMapping: React.Dispatch<React.SetStateAction<ColumnMapping[]>>;
  validationErrors: ValidationError[];
  setValidationErrors: (errors: ValidationError[]) => void;
  validatedData: Client[];
  setValidatedData: (clients: Client[]) => void;
  customFields: any[];
  fieldsToCreate: Record<
    string,
    { name: string; type: "text" | "number" | "date" | "select"; options?: string[] }
  >;
  setFieldsToCreate: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        { name: string; type: "text" | "number" | "date" | "select"; options?: string[] }
      >
    >
  >;
  importErrors: any[];
  setImportErrors: (errors: any[]) => void;
  importStats: { success: number; failed: number };
  setImportStats: (stats: { success: number; failed: number }) => void;
}

const ImportContext = createContext<ImportContextProps | undefined>(undefined);

export const ImportProvider: React.FC<{ children: ReactNode; customFields: any[] }> = ({
  children,
  customFields,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [columns, setColumns] = useState<string[]>([]);
  const [fileData, setFileData] = useState<any[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping[]>([]);
  const [initialMapping, setInitialMapping] = useState<ColumnMapping[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [validatedData, setValidatedData] = useState<Client[]>([]);
  const [fieldsToCreate, setFieldsToCreate] = useState<
    Record<
      string,
      { name: string; type: "text" | "number" | "date" | "select"; options?: string[] }
    >
  >({});
  const [importErrors, setImportErrors] = useState<any[]>([]);
  const [importStats, setImportStats] = useState({ success: 0, failed: 0 });

  return (
    <ImportContext.Provider
      value={{
        activeStep,
        setActiveStep,
        file,
        setFile,
        columns,
        setColumns,
        fileData,
        setFileData,
        columnMapping,
        setColumnMapping,
        initialMapping,
        setInitialMapping,
        validationErrors,
        setValidationErrors,
        validatedData,
        setValidatedData,
        customFields,
        fieldsToCreate,
        setFieldsToCreate,
        importErrors,
        setImportErrors,
        importStats,
        setImportStats,
      }}
    >
      {children}
    </ImportContext.Provider>
  );
};

export const useImportContext = () => {
  const ctx = useContext(ImportContext);
  if (!ctx) throw new Error("useImportContext must be used within ImportProvider");
  return ctx;
};
